import {
  Alert,
  Button,
  Card,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useContext, useMemo, useState } from 'react';
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { SuperAdminContext, SuperAdminScope } from 'src/components';
import { usePageHeader } from 'src/hooks/misc';
import { useDebouncedValue } from 'src/hooks/misc';
import { useSuperAdminApiOverrides } from 'src/hooks/superAdmin';
import { api, getApiErrorMessage } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import OrganizationFilter from '../OrganizationFilter';
import FaxServiceRequestMappingPage from './FaxServiceRequestMapping.page.tsx';

type FaxAvailableNumber = {
  phone_number: string;
  location?: string;
  number_type?: string;
  upfront_cost?: string | number | null;
  monthly_cost?: string | number | null;
};

type FaxApplication = {
  id?: string | number;
  application_name?: string;
  active?: boolean;
  webhook_event_url?: string;
};

type FaxOrgNumberItem = {
  agent?: {
    agent_id?: string;
    name?: string;
    email?: string;
    external_id?: string;
    org_uuid?: string;
    product?: string;
  };
  telnyx_phone_number?: {
    id?: string;
    phone_number?: string;
    phone_number_type?: string;
    connection_id?: string;
    connection_name?: string;
    status?: string;
    purchased_at?: string;
    country_iso_alpha2?: string;
  } | null;
};

const normalizeOrgNumbers = (payload: any): FaxOrgNumberItem[] => {
  const rawItems = payload?.fax_number_orgs || payload?.data || payload || [];
  if (!Array.isArray(rawItems)) {
    return [];
  }
  return rawItems;
};

type FaxAreaCodeOption = {
  area_code?: string;
  administrative_area?: string;
  country?: string;
};

type FaxRegionOption = {
  locality?: string;
  administrative_area?: string;
  country?: string;
};

type FaxAreaCodePage = {
  area_codes?: FaxAreaCodeOption[];
  data?: FaxAreaCodeOption[];
  page?: number;
  page_size?: number;
  total?: number;
  count?: number;
};

type FaxRegionPage = {
  regions?: FaxRegionOption[];
  data?: FaxRegionOption[];
  page?: number;
  page_size?: number;
  total?: number;
  count?: number;
};

const REGION_PAGE_SIZE = 50;
const AREA_CODE_PAGE_SIZE = 50;

const normalizeAreaCodes = (payload: any): FaxAreaCodeOption[] => {
  const rawItems = payload?.area_codes || payload?.data || payload || [];
  if (!Array.isArray(rawItems)) {
    return [];
  }
  return rawItems.map((item) => {
    if (typeof item === 'string') {
      return { area_code: item };
    }
    return item;
  });
};

const normalizeRegions = (payload: any): FaxRegionOption[] => {
  const rawItems = payload?.regions || payload?.data || payload || [];
  if (!Array.isArray(rawItems)) {
    return [];
  }
  return rawItems.map((item) => {
    if (typeof item === 'string') {
      return { locality: item };
    }
    return item;
  });
};

const normalizeAvailableNumbers = (payload: any): FaxAvailableNumber[] => {
  const rawNumbers =
    payload?.available_numbers ||
    payload?.numbers ||
    payload?.data ||
    payload ||
    [];

  if (!Array.isArray(rawNumbers)) {
    return [];
  }

  return rawNumbers
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'string') {
        return { phone_number: item };
      }
      if (item.phone_number) {
        return item;
      }
      if (item.number) {
        return { phone_number: item.number, ...item };
      }
      return null;
    })
    .filter(Boolean) as FaxAvailableNumber[];
};

const normalizeApplications = (payload: any): FaxApplication[] => {
  const rawApps =
    payload?.fax_applications ||
    payload?.applications ||
    payload?.data ||
    payload ||
    [];

  if (!Array.isArray(rawApps)) {
    return [];
  }

  return rawApps;
};

const getApplicationId = (application?: FaxApplication) => {
  if (!application) return undefined;
  return application.id;
};

const FaxContent = () => {
  const { org } = useContext(SuperAdminContext);
  const orgUuid = org?.org_uuid;
  const { headers: superAdminHeaders } = useSuperAdminApiOverrides();
  const queryClient = useQueryClient();
  const hasOrgSelected = Boolean(orgUuid);

  const [areaCode, setAreaCode] = useState('');
  const [searchBy, setSearchBy] = useState<'area_code' | 'region'>('area_code');
  const [region, setRegion] = useState('');
  const [areaCodeSearchValue, setAreaCodeSearchValue] = useState('');
  const [regionSearchValue, setRegionSearchValue] = useState('');
  const [searchParams, setSearchParams] = useState<{
    type: 'area_code' | 'region';
    value: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<
    'available' | 'org' | 'service-request-mapping'
  >('org');
  const [selectedApplicationId, setSelectedApplicationId] = useState<
    string | number | undefined
  >(undefined);
  const [isProvisionModalOpen, setIsProvisionModalOpen] = useState(false);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState<string | null>(
    null
  );

  usePageHeader({
    title: 'Fax',
  });

  const headers = useMemo(
    () => ({
      ...(superAdminHeaders || {}),
    }),
    [superAdminHeaders]
  );

  const { data: faxApplications = [], isLoading: isLoadingApplications } =
    useQuery({
      queryKey: ['fax-applications'],
      queryFn: () =>
        api
          .get('/v2/fax-applications', { headers })
          .then((res) => normalizeApplications(res.data)),
    });

  const debouncedAreaCodeSearchValue = useDebouncedValue(
    areaCodeSearchValue,
    400
  );

  const {
    data: areaCodePages,
    isLoading: isLoadingAreaCodes,
    isFetching: isFetchingAreaCodes,
    isFetchingNextPage: isFetchingAreaCodesNextPage,
    fetchNextPage: fetchNextAreaCodePage,
    hasNextPage: hasMoreAreaCodes,
  } = useInfiniteQuery<
    FaxAreaCodePage,
    Error,
    InfiniteData<FaxAreaCodePage>,
    string[],
    number
  >({
    queryKey: ['fax-area-codes', debouncedAreaCodeSearchValue],
    enabled: hasOrgSelected,
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => {
      const params: Record<string, string | number> = {
        page: pageParam,
        page_size: AREA_CODE_PAGE_SIZE,
      };
      const trimmedSearch = debouncedAreaCodeSearchValue.trim();
      if (trimmedSearch) {
        params.q = trimmedSearch;
      }
      return api
        .get('/v2/fax-numbers/area-codes', { headers, params })
        .then((res) => res.data);
    },
    getNextPageParam: (lastPage) => {
      const page = Number(lastPage?.page || 1);
      const pageSize = Number(lastPage?.page_size || AREA_CODE_PAGE_SIZE);
      const total = Number(lastPage?.total || 0);
      if (!total || page * pageSize >= total) {
        return undefined;
      }
      return page + 1;
    },
  });

  const debouncedRegionSearchValue = useDebouncedValue(regionSearchValue, 400);

  const {
    data: regionPages,
    isLoading: isLoadingRegions,
    isFetching: isFetchingRegions,
    isFetchingNextPage: isFetchingRegionsNextPage,
    fetchNextPage: fetchNextRegionPage,
    hasNextPage: hasMoreRegions,
  } = useInfiniteQuery<
    FaxRegionPage,
    Error,
    InfiniteData<FaxRegionPage>,
    string[],
    number
  >({
    queryKey: ['fax-regions', debouncedRegionSearchValue],
    enabled: hasOrgSelected,
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => {
      const params: Record<string, string | number> = {
        page: pageParam,
        page_size: REGION_PAGE_SIZE,
      };
      const trimmedSearch = debouncedRegionSearchValue.trim();
      if (trimmedSearch) {
        params.q = trimmedSearch;
      }
      return api
        .get('/v2/fax-numbers/regions', { headers, params })
        .then((res) => res.data);
    },
    getNextPageParam: (lastPage) => {
      const page = Number(lastPage?.page || 1);
      const pageSize = Number(lastPage?.page_size || REGION_PAGE_SIZE);
      const total = Number(lastPage?.total || 0);
      if (!total || page * pageSize >= total) {
        return undefined;
      }
      return page + 1;
    },
  });

  const regions = useMemo<FaxRegionOption[]>(() => {
    if (!regionPages?.pages?.length) {
      return [];
    }
    return regionPages.pages.flatMap((pageItem) => normalizeRegions(pageItem));
  }, [regionPages]);

  const areaCodes = useMemo<FaxAreaCodeOption[]>(() => {
    if (!areaCodePages?.pages?.length) {
      return [];
    }
    return areaCodePages.pages.flatMap((pageItem) =>
      normalizeAreaCodes(pageItem)
    );
  }, [areaCodePages]);

  const areaCodeOptions = useMemo(
    () =>
      areaCodes
        .map((item) => {
          const code = item?.area_code;
          if (!code) return null;
          return {
            value: code,
            label: code,
          };
        })
        .filter(Boolean) as Array<{ value: string; label: string }>,
    [areaCodes]
  );

  const regionOptions = useMemo(
    () =>
      regions
        .map((item) => {
          const locality = item?.locality;
          if (!locality) return null;
          const administrative_area = item?.administrative_area;
          if (!administrative_area) return null;
          return {
            value: `${locality}, ${administrative_area}`,
            label: `${locality}, ${administrative_area}`,
          };
        })
        .filter(Boolean) as Array<{ value: string; label: string }>,
    [regions]
  );

  const applicationOptions = useMemo(
    () =>
      faxApplications
        .filter((application) => application?.active)
        .map((application) => {
          const applicationId = getApplicationId(application);
          if (!applicationId) return null;
          return {
            value: applicationId,
            label:
              application.application_name || `Application ${applicationId}`,
            application,
          };
        })
        .filter(Boolean) as Array<{
        value: string | number;
        label: string;
        application: FaxApplication;
      }>,
    [faxApplications]
  );

  const selectedApplication = useMemo(
    () =>
      applicationOptions.find(
        (option) => option.value === selectedApplicationId
      )?.application,
    [applicationOptions, selectedApplicationId]
  );

  const {
    data: availableNumbers = [],
    isFetching: isFetchingNumbers,
    isLoading: isLoadingNumbers,
  } = useQuery({
    queryKey: ['fax-available-numbers', orgUuid, searchParams],
    enabled: activeTab === 'available' && Boolean(searchParams),
    queryFn: () => {
      const params: Record<string, string> = {};
      if (searchParams?.type === 'area_code') {
        params.area_code = searchParams.value;
      }
      if (searchParams?.type === 'region') {
        params.region = searchParams.value;
      }
      return api
        .get('/v2/fax-numbers/available', { headers, params })
        .then((res) => normalizeAvailableNumbers(res.data));
    },
  });

  const {
    data: orgFaxNumbers = [],
    isFetching: isFetchingOrgNumbers,
    isLoading: isLoadingOrgNumbers,
  } = useQuery({
    queryKey: ['fax-org-numbers', orgUuid],
    enabled: activeTab === 'org' && Boolean(orgUuid),
    queryFn: () =>
      api
        .get('/v2/fax-numbers/orgs', { headers })
        .then((res) => normalizeOrgNumbers(res.data)),
  });

  const provisionMutation = useMutation({
    mutationFn: async ({
      phoneNumber,
      applicationId,
    }: {
      phoneNumber: string;
      applicationId: string | number;
    }) => {
      const provisionPayload = {
        phone_number: phoneNumber,
        fax_application_id: applicationId,
      };

      await api.post('/v2/fax-numbers/provision', provisionPayload, {
        headers,
      });
    },
    onSuccess: () => {
      showToastSuccess('Fax number provisioned successfully.');
      setIsProvisionModalOpen(false);
      setPendingPhoneNumber(null);
      setSelectedApplicationId(undefined);
      queryClient.invalidateQueries({
        queryKey: ['fax-available-numbers'],
      });
      queryClient.invalidateQueries({
        queryKey: ['fax-org-numbers', orgUuid],
      });
    },
    onError: (error: any) => {
      showToastError(
        getApiErrorMessage(error) ||
          error?.message ||
          'Failed to provision fax number.'
      );
    },
  });

  const handleSearch = () => {
    if (searchBy === 'area_code') {
      const trimmedAreaCode = areaCode.trim();
      if (!trimmedAreaCode) {
        showToastError('Select an area code to search.');
        return;
      }
      setSearchParams({
        type: 'area_code',
        value: trimmedAreaCode,
      });
      return;
    }

    const trimmedValue = region.trim();
    if (!trimmedValue) {
      showToastError('Select a region to search.');
      return;
    }

    setSearchParams({
      type: 'region',
      value: trimmedValue,
    });
  };

  const openProvisionModal = (phoneNumber: string) => {
    setPendingPhoneNumber(phoneNumber);
    setIsProvisionModalOpen(true);
    if (!selectedApplicationId && applicationOptions.length > 0) {
      setSelectedApplicationId(applicationOptions[0].value);
    }
  };

  const handleProvisionConfirm = () => {
    if (!pendingPhoneNumber || !selectedApplicationId) {
      showToastError('Select a fax application to continue.');
      return;
    }
    provisionMutation.mutate({
      phoneNumber: pendingPhoneNumber,
      applicationId: selectedApplicationId,
    });
  };

  const formatPrice = (value?: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    const numericValue =
      typeof value === 'number' ? value : Number.parseFloat(value);
    if (Number.isNaN(numericValue)) {
      return `${value}`;
    }
    return `$${numericValue.toFixed(2)}`;
  };

  const availableNumbersColumns: ColumnsType<FaxAvailableNumber> = [
    {
      title: 'Fax Number',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (value) => value || 'N/A',
    },
    {
      title: 'Location/Rate Center',
      dataIndex: 'location',
      key: 'location',
      render: (value) => value || 'N/A',
    },
    {
      title: 'Number Type',
      dataIndex: 'number_type',
      key: 'number_type',
      render: (value) => {
        if (!value) return 'N/A';
        const normalized = `${value}`;
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
      },
    },
    {
      title: 'Upfront Price',
      dataIndex: 'upfront_cost',
      key: 'upfront_cost',
      render: (value) => formatPrice(value),
    },
    {
      title: 'Monthly Price',
      dataIndex: 'monthly_cost',
      key: 'monthly_cost',
      render: (value) => formatPrice(value),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          disabled={!hasOrgSelected}
          loading={provisionMutation.isPending}
          onClick={() => openProvisionModal(record.phone_number)}
        >
          Provision & attach
        </Button>
      ),
    },
  ];

  const orgNumbersColumns: ColumnsType<FaxOrgNumberItem> = [
    {
      title: 'Agent',
      dataIndex: 'agent',
      key: 'agent',
      render: (agent) => agent?.name || 'N/A',
    },
    {
      title: 'External ID',
      dataIndex: 'agent',
      key: 'external_id',
      render: (agent) => agent?.external_id || 'N/A',
    },
    {
      title: 'Fax Number',
      dataIndex: 'telnyx_phone_number',
      key: 'telnyx_phone_number',
      render: (value) => value?.phone_number || 'N/A',
    },
    {
      title: 'Connection',
      dataIndex: 'telnyx_phone_number',
      key: 'telnyx_connection',
      render: (value) => value?.connection_name || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'telnyx_phone_number',
      key: 'telnyx_status',
      render: (value) => value?.status || 'N/A',
    },
    {
      title: 'Purchased At',
      dataIndex: 'telnyx_phone_number',
      key: 'telnyx_purchased_at',
      render: (value) => value?.purchased_at || 'N/A',
    },
  ];

  return (
    <div className="page-wrapper">
      <OrganizationFilter />

      {!hasOrgSelected && (
        <div className="mt-4">
          <Alert
            message="Select an organization to provision and attach fax numbers."
            type="info"
            showIcon
          />
        </div>
      )}

      <div className="mt-4 flex flex-col gap-4">
        <div className="inline-flex">
          <Segmented
            options={[
              { label: 'Organization fax numbers', value: 'org' },
              { label: 'Available numbers', value: 'available' },
              {
                label: 'Service request mapping',
                value: 'service-request-mapping',
              },
            ]}
            value={activeTab}
            onChange={(value) =>
              setActiveTab(
                value as 'available' | 'org' | 'service-request-mapping'
              )
            }
            style={{ width: 'fit-content' }}
          />
        </div>

        {activeTab === 'available' && (
          <>
            <Card
              title="Fax number provisioning"
              bordered
              className="shadow-sm"
            >
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Typography.Text strong>Search by</Typography.Text>
                  <Space wrap>
                    <Select
                      style={{ width: 200 }}
                      options={[
                        { label: 'Area code', value: 'area_code' },
                        { label: 'Region', value: 'region' },
                      ]}
                      value={searchBy}
                      onChange={(value) => {
                        setSearchBy(value as 'area_code' | 'region');
                        setRegion('');
                        setAreaCode('');
                        setAreaCodeSearchValue('');
                        setRegionSearchValue('');
                      }}
                      disabled={!hasOrgSelected}
                    />
                    {searchBy === 'area_code' ? (
                      <Select
                        style={{ width: 200 }}
                        placeholder="Select..."
                        options={areaCodeOptions}
                        loading={
                          isLoadingAreaCodes ||
                          isFetchingAreaCodes ||
                          isFetchingAreaCodesNextPage
                        }
                        showSearch
                        filterOption={false}
                        searchValue={areaCodeSearchValue}
                        onSearch={(value) => {
                          setAreaCodeSearchValue(value);
                        }}
                        onPopupScroll={(event) => {
                          if (
                            !hasMoreAreaCodes ||
                            isFetchingAreaCodesNextPage
                          ) {
                            return;
                          }
                          const target = event.target as HTMLDivElement;
                          if (
                            target.scrollTop + target.clientHeight >=
                            target.scrollHeight - 24
                          ) {
                            fetchNextAreaCodePage();
                          }
                        }}
                        disabled={!hasOrgSelected}
                        value={areaCode || undefined}
                        onChange={(value) => {
                          setAreaCode(value as string);
                          setAreaCodeSearchValue('');
                        }}
                        allowClear
                      />
                    ) : (
                      <Select
                        style={{ width: 200 }}
                        placeholder="Select..."
                        options={regionOptions}
                        loading={
                          isLoadingRegions ||
                          isFetchingRegions ||
                          isFetchingRegionsNextPage
                        }
                        showSearch
                        filterOption={false}
                        searchValue={regionSearchValue}
                        onSearch={(value) => {
                          setRegionSearchValue(value.toUpperCase());
                        }}
                        onPopupScroll={(event) => {
                          if (!hasMoreRegions || isFetchingRegionsNextPage) {
                            return;
                          }
                          const target = event.target as HTMLDivElement;
                          if (
                            target.scrollTop + target.clientHeight >=
                            target.scrollHeight - 24
                          ) {
                            fetchNextRegionPage();
                          }
                        }}
                        disabled={!hasOrgSelected}
                        value={region || undefined}
                        onChange={(value) => {
                          setRegion(value as string);
                          setRegionSearchValue('');
                        }}
                        allowClear
                      />
                    )}
                    <Button
                      type="primary"
                      onClick={handleSearch}
                      disabled={!hasOrgSelected}
                      loading={isFetchingNumbers}
                    >
                      Search available numbers
                    </Button>
                  </Space>
                </div>

                <Typography.Text type="secondary">
                  Available numbers are scoped to the selected organization.
                  Select a number to provision and attach it in one step.
                </Typography.Text>
              </div>
            </Card>

            <Card title="Available fax numbers" bordered className="shadow-sm">
              <Table
                rowKey="phone_number"
                columns={availableNumbersColumns}
                dataSource={availableNumbers}
                loading={isLoadingNumbers || isFetchingNumbers}
                pagination={{ pageSize: 10 }}
                locale={{
                  emptyText: searchParams
                    ? 'No numbers available for this search.'
                    : 'Search for available numbers to see results.',
                }}
              />
            </Card>
            <Modal
              title="Provision fax number"
              open={isProvisionModalOpen}
              centered
              onCancel={() => {
                setIsProvisionModalOpen(false);
                setPendingPhoneNumber(null);
                setSelectedApplicationId(undefined);
              }}
              onOk={handleProvisionConfirm}
              okText="Provision & attach"
              confirmLoading={provisionMutation.isPending}
              destroyOnClose
            >
              <div className="flex flex-col gap-3">
                <Typography.Text>
                  Confirm provisioning for{' '}
                  <Typography.Text strong>
                    {pendingPhoneNumber || 'selected number'}
                  </Typography.Text>
                </Typography.Text>
                <div className="flex flex-col gap-2">
                  <Typography.Text strong>Fax application</Typography.Text>
                  <Select
                    style={{ minWidth: 260 }}
                    loading={isLoadingApplications}
                    placeholder="Select fax application"
                    options={applicationOptions}
                    value={selectedApplicationId}
                    onChange={(value) =>
                      setSelectedApplicationId(value as string | number)
                    }
                  />
                  {selectedApplication?.webhook_event_url && (
                    <Typography.Text type="secondary">
                      Webhook: {selectedApplication.webhook_event_url}
                    </Typography.Text>
                  )}
                  {applicationOptions.length === 0 &&
                    !isLoadingApplications && (
                      <Alert
                        type="warning"
                        showIcon
                        message="No fax applications available. Provisioning requires an application to configure the webhook."
                      />
                    )}
                </div>
              </div>
            </Modal>
          </>
        )}

        {activeTab === 'org' && (
          <Card title="Organization fax numbers" bordered className="shadow-sm">
            <Table
              rowKey={(record) => record?.agent?.agent_id as string}
              columns={orgNumbersColumns}
              dataSource={orgFaxNumbers}
              loading={isLoadingOrgNumbers || isFetchingOrgNumbers}
              pagination={{ pageSize: 10 }}
              locale={{
                emptyText: hasOrgSelected
                  ? 'No fax numbers found for this organization.'
                  : 'Select an organization to view fax numbers.',
              }}
            />
          </Card>
        )}

        {activeTab === 'service-request-mapping' && (
          <FaxServiceRequestMappingPage />
        )}
      </div>
    </div>
  );
};

const FaxPage = () => (
  <SuperAdminScope>
    <FaxContent />
  </SuperAdminScope>
);

export default FaxPage;
